import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from './ApiConfig';

/**
 * AudioPlayer Component
 * Displays an audio player for answers that have an associated audio file
 * 
 * @param {string} file_key - The file key to fetch the signed URL for
 */
const AudioPlayer = ({ file_key }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (!file_key) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(API_ENDPOINTS.SIGN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_key: file_key })
        });

        if (!response.ok) {
          throw new Error(`Failed to sign URL: ${response.status}`);
        }

        const data = await response.json();
        const signedUrl = data[0]?.signed_url;

        if (!signedUrl) {
          throw new Error('No signed URL returned from server');
        }

        setAudioUrl(signedUrl);
      } catch (err) {
        console.error('Error fetching signed audio URL:', err);
        setError('Failed to load audio');
      } finally {
        setLoading(false);
      }
    };

    fetchSignedUrl();
  }, [file_key]);

  if (!file_key) {
    return null;
  }

  if (loading) {
    return (
      <div className="mt-2 p-3 bg-blue-50 rounded-md flex items-center gap-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm text-gray-600">Loading audio...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-2 p-3 bg-red-50 rounded-md">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (!audioUrl) {
    return null;
  }

  return (
    <div className="mt-2 p-3 bg-blue-50 rounded-md">
      <audio 
        controls 
        className="w-full"
        style={{ maxWidth: '100%', height: '40px' }}
      >
        <source src={audioUrl} type="audio/mpeg" />
        <source src={audioUrl} type="audio/wav" />
        <source src={audioUrl} type="audio/ogg" />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

export default AudioPlayer;
